// Modal shown after a survey is successfully created.
// Displays the voter link (with copy), the admin link (warned), and a
// download button for the privacy file. Closes with click-outside or Escape.

import type { CreateSurveyResponse } from '../api';
import type { Appointment, FinderConfig } from '../types';
import { downloadPrivacyFile } from '../privacy-file';
import { buildVoterShareMessage, type ShareMessage } from '../share-message';
import { el } from './dom';

export type CreateModalContext = {
  result: CreateSurveyResponse;
  title: string | null;
  config: FinderConfig;
  slots: Appointment[];
  origin: string;
  location?: string | null;
};

export function showCreateModal(ctx: CreateModalContext): void {
  const adminUrl = `${ctx.origin}/admin/${ctx.result.admin_token}`;
  const voterUrl = `${ctx.origin}/vote/${ctx.result.voter_token}`;

  const overlay = el('div', { class: 'modal-overlay', role: 'dialog', 'aria-modal': 'true' });
  const dialog = el('div', { class: 'modal-dialog' });

  const heading = el('h2', { class: 'modal-heading' }, 'Umfrage erstellt');
  const intro = el(
    'p',
    { class: 'modal-intro' },
    'Lade jetzt die Privacy-Datei herunter und speichere den Teilnehmer-Link, ',
    'bevor du dieses Fenster schließt.',
  );

  // Voter link (primary action) — Web-Share-API liefert „in WhatsApp/Signal
  // teilen" auf Mobile, ohne Drittanbieter-SDK. Die Share-Message ist auf
  // WhatsApp-Formatierung optimiert (*fett* / _kursiv_), aber sieht auch in
  // Plain-Text-Apps aufgeräumt aus.
  const share = buildVoterShareMessage({
    title: ctx.title,
    config: ctx.config,
    slots: ctx.slots,
    voterUrl,
    location: ctx.location ?? null,
  });
  const voterSection = renderLinkSection({
    label: 'Teilnehmer-Link',
    badgeText: 'Teilen',
    badgeClass: 'badge-voter',
    url: voterUrl,
    description: 'Schicke diesen Link an alle, die abstimmen sollen.',
    share,
  });

  // Admin link (secondary)
  const adminSection = renderLinkSection({
    label: 'Admin-Zugang',
    badgeText: 'Nicht teilen',
    badgeClass: 'badge-admin',
    url: adminUrl,
    description: 'Vollzugriff auf Antworten und Bearbeiten. Privat halten!',
  });

  // Privacy file action
  const downloadBtn = el(
    'button',
    { type: 'button', class: 'btn-primary modal-download' },
    '⬇  Privacy-Datei herunterladen',
  ) as HTMLButtonElement;
  downloadBtn.addEventListener('click', () => {
    downloadPrivacyFile({
      title: ctx.title,
      origin: ctx.origin,
      surveyId: ctx.result.id,
      adminToken: ctx.result.admin_token,
      voterToken: ctx.result.voter_token,
      expiresAt: ctx.result.expires_at,
      createdAt: Math.floor(Date.now() / 1000),
      config: ctx.config,
      slots: ctx.slots,
    });
  });

  const openAdminBtn = el(
    'a',
    { href: adminUrl, class: 'btn-secondary modal-open-admin' },
    'Admin-Bereich öffnen →',
  );

  const closeBtn = el(
    'button',
    { type: 'button', class: 'btn-secondary modal-close' },
    'Schließen',
  );
  closeBtn.addEventListener('click', () => closeModal(overlay));

  const actionRow = el(
    'div',
    { class: 'modal-actions' },
    downloadBtn,
    openAdminBtn,
    closeBtn,
  );

  dialog.append(heading, intro, voterSection, adminSection, actionRow);
  overlay.append(dialog);
  document.body.append(overlay);

  // Close on Escape or click outside the dialog.
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') closeModal(overlay, onKey);
  };
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeModal(overlay, onKey);
  });
  window.addEventListener('keydown', onKey);
}

function closeModal(overlay: HTMLElement, onKey?: (e: KeyboardEvent) => void): void {
  if (onKey) window.removeEventListener('keydown', onKey);
  overlay.remove();
}

function renderLinkSection(opts: {
  label: string;
  badgeText: string;
  badgeClass: string;
  url: string;
  description: string;
  /** Wenn gesetzt: zeigt Web-Share + WhatsApp + Mail-Direktlinks. */
  share?: ShareMessage;
}): HTMLElement {
  const section = el('section', { class: 'modal-link-section' });

  const heading = el('div', { class: 'modal-link-header' });
  heading.append(
    el('span', { class: 'modal-link-label' }, opts.label),
    el('span', { class: `modal-badge ${opts.badgeClass}` }, opts.badgeText),
  );

  // Klickbarer Direkt-Link — wie in der Privacy-Datei. Öffnet die Seite in
  // einem neuen Tab. Darunter dann das Eingabefeld zum Kopieren.
  const directLink = el('a', {
    class: 'modal-link-direct',
    href: opts.url,
    target: '_blank',
    rel: 'noopener noreferrer',
  }, opts.url);

  const urlBox = el('div', { class: 'modal-url-box' });
  const urlText = el('input', {
    type: 'text',
    class: 'modal-url-input',
    readonly: 'readonly',
    value: opts.url,
  }) as HTMLInputElement;
  urlText.addEventListener('focus', () => urlText.select());

  const copyBtn = el(
    'button',
    { type: 'button', class: 'modal-copy-btn', 'aria-label': 'Link kopieren' },
    'Kopieren',
  ) as HTMLButtonElement;
  copyBtn.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(opts.url);
      copyBtn.textContent = '✓ Kopiert';
      setTimeout(() => (copyBtn.textContent = 'Kopieren'), 1500);
    } catch {
      // fallback: select the input so the user can copy manually
      urlText.select();
    }
  });

  urlBox.append(urlText, copyBtn);

  // Web-Share-API (Mac/iOS/Android: System-Share-Sheet). Nur wenn Browser
  // es kann. Auf manchen Mac-Browsern ist das Share-Sheet auf System-Apps
  // begrenzt — deshalb bieten wir zusätzlich direkte WhatsApp- und Mail-
  // Knöpfe an, die OS-unabhängig funktionieren.
  if (opts.share && typeof navigator.share === 'function') {
    const shareBtn = el(
      'button',
      { type: 'button', class: 'modal-copy-btn modal-share-btn', 'aria-label': 'Link teilen' },
      '↗ Teilen',
    ) as HTMLButtonElement;
    shareBtn.addEventListener('click', async () => {
      try {
        await navigator.share({
          title: opts.share!.title,
          text: opts.share!.text,
          url: opts.share!.url,
        });
      } catch { /* User-Cancel */ }
    });
    urlBox.append(shareBtn);
  }

  const description = el('p', { class: 'modal-link-description' }, opts.description);
  section.append(heading, directLink, urlBox);

  // Direkt-Knöpfe für WhatsApp und Mail — funktionieren auf jedem System,
  // weil sie URL-Schemen sind. Die Nachricht ist die Rich-Variante mit Titel,
  // Datum, Uhrzeit, Slot-Anzahl.
  if (opts.share) {
    const direct = el('div', { class: 'modal-direct-share' });
    direct.append(
      el('a', {
        class: 'modal-direct-btn',
        href: opts.share.whatsapp,
        target: '_blank',
        rel: 'noopener noreferrer',
      }, '🟢 WhatsApp'),
      el('a', {
        class: 'modal-direct-btn',
        href: opts.share.mailto,
      }, '✉️ Mail'),
    );
    section.append(direct);
  }

  section.append(description);
  return section;
}
